/**
 * WorkOrdersTab — Phase: multi-WO per project.
 * Inside ProjectDetail page, this tab manages the list of work orders for the
 * current project. Each WO is identified as `{site_code}.{seq}` (e.g. NSK01.1).
 * Attendance + employee assignments cascade off the selected WO.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, Briefcase, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';

const INP = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#3b4872] focus:border-[#3b4872]';

const STATUS_OPTS = ['Active', 'On Hold', 'Completed', 'Cancelled'];

export default function WorkOrdersTab({ project, reload }) {
  const [wos, setWos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/projects/${project.id}/work-orders`);
      setWos(d.work_orders || []);
    } catch { toast.error('Failed to load work orders'); }
    setLoading(false);
  };

  useEffect(() => { if (project?.id) load(); }, [project?.id]);

  const save = async (wo) => {
    try {
      if (wo.id) {
        await apiPut(`/projects/${project.id}/work-orders/${wo.id}`, wo);
        toast.success('Work order updated');
      } else {
        await apiPost(`/projects/${project.id}/work-orders`, wo);
        toast.success('Work order added');
      }
      setEditing(null);
      load();
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (wo) => {
    if (!window.confirm(`Delete work order ${wo.code}? This will detach all linked employees and attendance rows.`)) return;
    try {
      await apiDel(`/projects/${project.id}/work-orders/${wo.id}`);
      toast.success('Work order deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const siteCode = project?.site_code || project?.code || '';
  const totalValue = wos.reduce((acc, w) => acc + (parseFloat(w.value) || 0), 0);

  return (
    <div className="space-y-5" data-testid="work-orders-tab">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Project</div>
          <h2 className="font-display text-xl font-extrabold tracking-tight">Work Orders for {siteCode}</h2>
          <p className="text-sm text-slate-600 mt-1">
            Add each separate work order issued by the client under this project. Work order codes auto-increment as <code className="bg-slate-100 px-1">{siteCode}.1</code>, <code className="bg-slate-100 px-1">{siteCode}.2</code>, … Attendance &amp; employee assignments cascade off the selected WO.
          </p>
        </div>
        <button
          onClick={() => setEditing({ code: '', title: '', value: 0, status: 'Active', start_date: '', end_date: '' })}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-sm font-semibold"
          data-testid="add-wo-btn"
        >
          <Plus size={14} /> Add Work Order
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : wos.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
          <Briefcase size={28} className="text-slate-300" />
          <span>No work orders added yet.</span>
          <span className="text-[11px] text-slate-400">Click <b>Add Work Order</b> above to register the first WO under {siteCode}.</span>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase font-bold tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Code</th>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-right">Value (₹)</th>
                <th className="px-4 py-2 text-left">Start → End</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {wos.map((wo) => (
                <tr key={wo.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono font-bold text-[#3b4872]">{wo.code}</td>
                  <td className="px-4 py-2.5">{wo.title || '—'}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{(wo.value || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{wo.start_date || '—'} → {wo.end_date || '—'}</td>
                  <td className="px-4 py-2.5 text-xs">
                    <StatusPill status={wo.status} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(wo)} className="p-1.5 hover:bg-slate-200 rounded-sm" data-testid={`edit-wo-${wo.code}`}>
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => del(wo)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-sm">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="px-4 py-2.5" colSpan={2}>Total ({wos.length} WO)</td>
                <td className="px-4 py-2.5 text-right font-mono">₹{totalValue.toLocaleString('en-IN')}</td>
                <td colSpan={3}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <WorkOrderModal
          initial={editing}
          siteCode={siteCode}
          nextSeq={wos.length + 1}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    Active: 'bg-slate-100 text-slate-700 border-slate-300',
    'On Hold': 'bg-slate-100 text-slate-500 border-slate-300',
    Completed: 'bg-slate-200 text-slate-800 border-slate-400 font-bold',
    Cancelled: 'bg-slate-100 text-slate-400 line-through border-slate-300',
  }[status] || 'bg-slate-100 text-slate-700';
  return <span className={`inline-block px-2 py-0.5 border rounded-sm text-[11px] ${tone}`}>{status || '—'}</span>;
}

function WorkOrderModal({ initial, siteCode, nextSeq, onClose, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    code: initial.code || `${siteCode}.${nextSeq}`,
  });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">{initial.id ? 'Edit' : 'Add'} Work Order</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Work Order Code *">
            <input
              value={form.code}
              onChange={e => u('code', e.target.value.toUpperCase())}
              className={INP}
              placeholder={`${siteCode}.${nextSeq}`}
              data-testid="wo-code"
            />
            <p className="text-[10px] text-slate-500 mt-1">Auto-numbered as <b>{siteCode}.{nextSeq}</b>. Override only when you have a custom client reference.</p>
          </Field>
          <Field label="Title">
            <input value={form.title || ''} onChange={e => u('title', e.target.value)} className={INP} placeholder="e.g. PEB shed for warehouse extension" data-testid="wo-title" />
          </Field>
          <Field label="Description">
            <textarea value={form.description || ''} onChange={e => u('description', e.target.value)} className={INP + ' min-h-[60px]'} rows="2" placeholder="Scope summary (optional)" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Value (₹)">
              <input type="number" value={form.value || ''} onChange={e => u('value', +e.target.value)} className={INP} data-testid="wo-value" />
            </Field>
            <Field label="Status">
              <select value={form.status || 'Active'} onChange={e => u('status', e.target.value)} className={INP}>
                {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Start Date">
              <input type="date" value={form.start_date || ''} onChange={e => u('start_date', e.target.value)} className={INP} />
            </Field>
            <Field label="End Date">
              <input type="date" value={form.end_date || ''} onChange={e => u('end_date', e.target.value)} className={INP} />
            </Field>
            <Field label="Client Ref">
              <input value={form.client_ref || ''} onChange={e => u('client_ref', e.target.value)} className={INP} placeholder="Client's WO number" />
            </Field>
          </div>
          {!initial.id && (
            <div className="flex items-start gap-2 p-3 bg-indigo-50/40 border border-indigo-200 rounded-sm text-xs text-slate-700">
              <AlertCircle size={14} className="mt-0.5 text-[#3b4872] shrink-0" />
              <span>After saving, edit any employee from <b>HR → Employees</b> and pick this work order in the project field. Their attendance from that date will be tagged with this WO.</span>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-sm text-sm">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-sm font-semibold flex items-center gap-1.5" data-testid="save-wo">
            <Save size={14} /> {initial.id ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
